import React, { forwardRef, useEffect } from "react";
import { Channel, ChannelProps, SuggestionListItem, MessageStatus, useChatContext, useMessageContext, ReactionSelector, ReactionsList, defaultReactionOptions, CustomMessageActions } from "stream-chat-react";
import CustomAttachment from "./CustomAttachment";
import CustomAttachmentSelector from "./CustomAttachmentSelector";
import { EmojiPicker } from "stream-chat-react/emojis";
import { useNavigate, useParams } from "react-router-dom";
import { ChatChannelType } from "../../../models/ChatChannel";
import { useAuth } from "../../../hooks/useAuth";
import mixpanel from "../../../utils/MixPanel";
import { AuthenticationStatusGetStream } from "../../../store/slices/authSlice";
import { User } from "stream-chat";
import { useStream } from "../../../hooks/useStream";
import { CustomQuotedMessage } from "./CustomQuotedMessage";
import { CustomQuotedMessagePreview } from "./CustomQuotedMessagePreview";
import { CustomMessage } from "./CustomMessage";
import { CustomMessageActionList } from "./CustomMessageActionList";
import { CustomMessageSystem } from "./CustomMessageSystem";

interface CustomChannelProps extends ChannelProps {
    children: React.ReactNode;
}

const customReactionOptions = [
    {
        type: "haha",
        Component: () => <>🤣</>,
        name: "Haha",
    },
    {
        type: "like",
        Component: () => <>👍</>,
        name: "Thumbs Up",
    },
    {
        type: "love",
        Component: () => <>🧡</>,
        name: "Heart",
    },
    {
        type: "sad",
        Component: () => <>👎</>,
        name: "Thumbs Down",
    },
    {
        type: "fire",
        Component: () => <>🔥</>,
        name: "Fire",
    },
    {
        type: "rocket",
        Component: () => <>🚀</>,
        name: "Rocket",
    },
    {
        type: "wow",
        Component: () => <>😮</>,
        name: "Wow",
    },
    {
        type: "pray",
        Component: () => <>🙏</>,
        name: "Pray",
    },
    {
        type: "cool",
        Component: () => <>🤙</>,
        name: "Cool",
    },
    {
        type: "100",
        Component: () => <>💯</>,
        name: "100",
    },
    {
        type: "fiesta",
        Component: () => <>🎉</>,
        name: "Fiesta",
    },
];

const compatibilityReactionOptions = [
    {
        type: "lmao",
        Component: () => <>🤣</>,
        name: "LMAO",
    },
    {
        type: "thumbsdown",
        Component: () => <>👎</>,
        name: "Thumbs Down",
    },
    {
        type: "thumbsup",
        Component: () => <>👍</>,
        name: "Thumbs Up",
    },
    {
        type: "heart",
        Component: () => <>❤️</>,
        name: "Heart",
    },
];

const CustomReactionsList = (props: any) => (
    <ReactionsList {...props} reactionOptions={[...compatibilityReactionOptions, ...customReactionOptions]} />
  );
  
  // ReactionSelector component requires forwarded reference
  const CustomReactionSelector = forwardRef((props: any, ref: any) => (
    <ReactionSelector
      {...props}
      ref={ref}
      reactionOptions={customReactionOptions}
    />
  ));

const CustomSuggestionItem = (props: any) => {
    if(props?.item?.description === 'Post a random gif to the channel') {
        props.item.description = 'Post a gif';
    }

    return <SuggestionListItem {...props} />
};

const CustomChannel = ({children}: CustomChannelProps) => {
    const { channel, client, setActiveChannel } = useChatContext();
    const { type, id } = useParams();
    const {statusGetStream} = useStream();
    const navigate = useNavigate();
    const {tab} = useParams();

    useEffect(() => {
        if(statusGetStream === AuthenticationStatusGetStream.LOGGED)
        {
            let channelType = '';

            switch (type) {
                case 'user':
                    channelType = ChatChannelType.Messaging;
                    break;
                case 'event':
                    channelType = ChatChannelType.Event;
                    break;
                case 'group':
                    channelType = ChatChannelType.Group;
                    break;
            }

            const channel = client.channel(channelType, id);
            setActiveChannel(channel);
        }
        else {
            setActiveChannel(undefined);
        }
    }, [id, type, client, setActiveChannel, statusGetStream]);

    useEffect(() => {
        mixpanel.track('WebApp - channel load', {
            channel: channel?.id
        });
    }, [channel]);

    const handleUserClick = (event: React.BaseSyntheticEvent, user?: User) => {
        if(user) {
            navigate(`/home/${tab}/user/${user.id}`);
        }
    }

    useEffect(() => {
        const selector = '.str-chat__message-reactions-details-reacting-user[data-custom-user-id]';

        const attachClickHandler = () => {
            const elements = document.querySelectorAll(selector);

            elements.forEach((element) => {
                const htmlElement = element as HTMLElement & {
                    dataset: DOMStringMap & {
                        customUserClickBound?: string;
                        customUserId?: string;
                    };
                };

                if (htmlElement.dataset.customUserClickBound === 'true') {
                    return;
                }

                const userId = htmlElement.dataset.customUserId;

                if (!userId) {
                    return;
                }

                const onClick = (event: Event) => {
                    handleUserClick(event as unknown as React.BaseSyntheticEvent, {
                        id: userId,
                    } as User);
                };

                htmlElement.dataset.customUserClickBound = 'true';
                htmlElement.style.cursor = 'pointer';
                htmlElement.addEventListener('click', onClick);
            });
        };

        attachClickHandler();

        const observer = new MutationObserver(() => {
            attachClickHandler();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [tab]);

    return <Channel 
        channel={channel}
        EmojiPicker={EmojiPicker}
        ReactionsList={CustomReactionsList}
        ReactionSelector={CustomReactionSelector}
        Attachment={CustomAttachment}
        AttachmentSelector={CustomAttachmentSelector}
        AutocompleteSuggestionItem={CustomSuggestionItem}
        activeUnreadHandler={() => {
            // Dont do anything
        }}
        onMentionsClick={handleUserClick}
        QuotedMessage={CustomQuotedMessage}
        QuotedMessagePreview={CustomQuotedMessagePreview}
        Message={CustomMessage}
        MessageSystem={CustomMessageSystem}
        CustomMessageActionsList={CustomMessageActionList}
    >
        {children}
    </Channel>
}

export default CustomChannel;
